import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import * as StateSelectors from 'State/selectors';
import { getSitesAction } from 'State/sites/actions';
import { SelectField } from 'Common/ReduxFormFields';
import { AlternateProvisionTypeEnum } from 'Common/Constants/Enumeration/AlternateProvisionTypeEnum';
import {
  YesNoAllEnum,
  AllOptionEnum,
} from 'Common/Constants/Enumeration/PropEnums';
import { addAllOption } from 'Common/FormHelper';
import { filterSites } from 'Common/Helpers';

/**
 * This is a wrapper around the redux-form SelectField adding additional features and default options for site selection.
 */
function SiteSelectFieldContainer({
  getSitesAction,
  stateSiteOptions,
  sitesLoading,
  loading,
  options,
  filter,
  active,
  licensed,
  primary,
  siteMasterTypeIds,
  alternateProvisionTypes,
  allOption,
  label,
  input,
  selectFirst,
  fetchAllSites,
  ...rest
}) {
  // 1. Use either the options passed in as a prop or automatically request some.
  //    If you plan to pass in a custom set of options, but they are loading, you should
  //    pass an empty array as a placeholder. Otherwise, sites will be requested.
  const [baseSiteOptions, setBaseSiteOptions] = React.useState(null);

  React.useEffect(() => {
    if (options === null) {
      if (stateSiteOptions.length === 0) {
        getSitesAction(fetchAllSites);
        return;
      }

      setBaseSiteOptions(stateSiteOptions);
      return;
    }

    setBaseSiteOptions(options);
  }, [options, stateSiteOptions]);

  // 2. Once we have a base set of sites we need to apply any custom filtering, options, or rules.
  const [siteOptions, setSiteOptions] = React.useState([]);

  React.useEffect(() => {
    if (!baseSiteOptions || baseSiteOptions.length === 0) {
      setSiteOptions([]);
      return;
    }

    // Filter
    let filteredOptions = filterSites(baseSiteOptions, {
      active,
      licensed,
      primary,
      siteMasterTypeIds,
      alternateProvisionTypes,
    });

    // Apply the custom filter prop if provided
    if (filter) {
      filteredOptions = filteredOptions.filter(filter);
    }

    // Map the options to a new form
    filteredOptions = filteredOptions.map((site, i) => ({
      value: site.Id,
      key: i + site.Id,
      text: site.Id + ' ' + site.Description,
      sitegroupid: site.SiteGroupId,
    }));

    // Auto select first
    if (filteredOptions.length === 1 || selectFirst) {
      input.onChange(filteredOptions[0].value);
    }

    // Add all option
    if (filteredOptions.length > 1) {
      filteredOptions = addAllOption(filteredOptions, allOption);
    }

    setSiteOptions(filteredOptions);
  }, [baseSiteOptions]);

  return (
    <SelectField
      options={siteOptions}
      loading={sitesLoading || loading}
      label={label}
      input={input}
      {...rest}
    />
  );
}

SiteSelectFieldContainer.propTypes = {
  sitesLoading: PropTypes.bool.isRequired,
  input: PropTypes.object.isRequired,
  options: PropTypes.array,
  filter: PropTypes.func,
  active: PropTypes.oneOf(Object.values(YesNoAllEnum)),
  licensed: PropTypes.oneOf(Object.values(YesNoAllEnum)),
  primary: PropTypes.oneOf(Object.values(YesNoAllEnum)),
  allOption: PropTypes.oneOf(Object.values(AllOptionEnum)),
  label: PropTypes.string,
  selectFirst: PropTypes.bool,
  siteMasterTypeIds: PropTypes.array,
  alternateProvisionTypes: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(AlternateProvisionTypeEnum))
  ),
  fetchAllSites: PropTypes.bool,
};

SiteSelectFieldContainer.defaultProps = {
  options: null,
  filter: null,
  active: YesNoAllEnum.Yes,
  licensed: YesNoAllEnum.Yes,
  primary: YesNoAllEnum.All,
  allOption: AllOptionEnum.All,
  label: 'Site',
  selectFirst: false,
  siteMasterTypeIds: [1],
  alternateProvisionTypes: null,
  fetchAllSites: false,
};

const mapStateToProps = (state) => {
  return {
    stateSiteOptions: StateSelectors.getSites(state),
    sitesLoading: StateSelectors.getSitesLoading(state),
  };
};

const mapDispatchToProps = {
  getSitesAction,
};

export const SiteSelectField = connect(
  mapStateToProps,
  mapDispatchToProps
)(SiteSelectFieldContainer);



// WEBPACK FOOTER //
// ./src/common/ReduxFormFields/SiteSelectField.js